import React, { useState, useEffect } from "react";
import { ArrowLeft, CheckCircle2, DollarSign, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import DateTimePicker from "components/generic/DateTimePicker";
import LocationPicker from "../generic/LocationPicker";
import PageTitle from "components/generic/PageTitle";
import { getEquipmentById, getCrew, assignEquipment } from "services/equipmentService";
import toast, { Toaster } from "react-hot-toast";

/* ----------------------------------------
       MAIN COMPONENT
---------------------------------------- */
const AssignEquipment = ({ onBack, equipmentId, equipmentName }) => {
  const [equipmentDetails, setEquipmentDetails] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await getEquipmentById(equipmentId);
        setEquipmentDetails(res.data?.data || res.data);
      } catch (err) {
        console.error("Failed to load equipment details:", err);
      }
    }
    if (equipmentId) load();
  }, [equipmentId]);

  /* ------------------- LOAD CREW ------------------- */
  const [crewList, setCrewList] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const fetchCrewList = async (query) => {
    try {
      const res = await getCrew(query);
      const list = res?.data?.data || res?.data || [];
      setCrewList(list);
      setIsDropdownOpen(list.length > 0);
    } catch (err) {
      console.error("Failed to load crew list:", err);
      setCrewList([]);
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setCrewList([]);
      setIsDropdownOpen(false);
      return;
    }
    fetchCrewList(searchQuery);
  }, [searchQuery]);

  /* ------------------- FORM STATES ------------------- */
  const [assignmentName, setAssignmentName] = useState(equipmentName || ""); // Controlled Input
  const [checkoutDateTime, setCheckoutDateTime] = useState(null);
  const [returnDateTime, setReturnDateTime] = useState(null);

  const [rentalType, setRentalType] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [notes, setNotes] = useState("");
  const [pickupLocation, setPickupLocation] = useState(null);
  const [errors, setErrors] = useState({});

  const [checklistItems, setChecklistItems] = useState([
    { id: 1, text: "Conduct a safety inspection", checked: false },
    { id: 2, text: "Check for any missing components", checked: false },
    { id: 3, text: "Test functionality of all controls", checked: false },
    { id: 4, text: "Ensure proper calibration is done", checked: false },
    { id: 5, text: "Review operational manuals with crew", checked: false },
    { id: 6, text: "Log equipment status for tracking", checked: false },
  ]);

  const [newItemText, setNewItemText] = useState("");

  // Helper to clear errors on change
  const handleFieldChange = (field, value, setter) => {
    setter(value);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const toggleChecklistItem = (id) => {
    setChecklistItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handleAddChecklistItem = () => {
    if (!newItemText.trim()) return;

    setChecklistItems([
      ...checklistItems,
      {
        id: Date.now(), // Still used for React keys and toggling
        text: newItemText,
        checked: false,
        isNew: true // <--- Add this flag
      },
    ]);
    setNewItemText("");
  };

  /* ------------------- RENTAL CALC ------------------- */
  useEffect(() => {
    if (!checkoutDateTime || !returnDateTime || !rentalType || !equipmentDetails)
      return;

    const start = new Date(checkoutDateTime);
    const end = new Date(returnDateTime);

    if (isNaN(start) || isNaN(end) || end <= start) {
      setTotalCost(0);
      setTotalHours(0);
      setTotalDays(0);
      return;
    }

    const diffMs = end - start;
    const hours = Math.ceil(diffMs / (1000 * 60 * 60));
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    setTotalHours(hours);
    setTotalDays(days);

    const dailyRate = Number(equipmentDetails.daily_rental_rate || 0);
    const hourlyRate = dailyRate / 24;

    if (rentalType === "hourly") setTotalCost(hours * hourlyRate);
    else setTotalCost(days * dailyRate);
  }, [checkoutDateTime, returnDateTime, rentalType, equipmentDetails]);

  /* ----------------------------------------
      CONFIRM ASSIGNMENT → API CALL
  ---------------------------------------- */
  const handleConfirmAssignment = async () => {
    if (isSubmitting) return;

    // --- Validation Logic ---
    const newErrors = {};
    if (!assignmentName.trim()) newErrors.assignmentName = "Required";
    if (!selectedCrew) newErrors.selectedCrew = "Crew required";
    if (!checkoutDateTime) newErrors.checkoutDateTime = "Required";
    if (!returnDateTime) newErrors.returnDateTime = "Required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setIsSubmitting(true);

    try {

      const storedUser = localStorage.getItem("user");
      const userObj = storedUser ? JSON.parse(storedUser) : null;
      const orgType = userObj?.organizationType;

      const formatDate = (d) => {
        const date = new Date(d);
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
      };

      const payload = {
        equipment_id: Number(equipmentId),
        project_id: null,
        crew_member_id: Number(selectedCrew),
        check_out_date: formatDate(checkoutDateTime),
        expected_return_date: formatDate(returnDateTime),
        pickup_location: pickupLocation?.address || "",
        notes: notes || "",
        send_email: true,
        rent_calculation: Number(totalCost?.toFixed(2)) || 0,
        checklist: checklistItems.map((i) => ({
          text: i.text,
          checked: i.checked,
          ...(i.isNew ? {} : { id: i.id }),
        })),
        ...(orgType === 2 && { organization_type: 2 }),
      };

      const res = await assignEquipment(payload);

      console.log("API RAW RESPONSE:", res);

      const apiData = res;

      if (apiData?.success === true) {
        toast.success("Equipment assigned successfully");

        setTimeout(() => {
          onBack();
        }, 800);
        return;
      }

      toast.error(apiData?.message || "Assignment failed");
      setIsSubmitting(false);

    } catch (err) {
      console.error("Assignment Error:", err);
      toast.error("Server error while assigning equipment");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <Toaster position="bottom-right" reverseOrder={false} />
      <Button
        variant="ghost"
        className="mb-6 pl-0 hover:pl-2 gap-2 hover:bg-transparent"
        onClick={onBack}
      >
        <ArrowLeft size={20} />
        Back to Equipment Management
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <PageTitle
            title="Assign Equipment"
            description={`Assign ${equipmentName} to a project and crew member`}
          />
          <div className="border border-border bg-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-6">
              Equipment Information
            </h2>
            <div className="space-y-6">
              {/* ASSIGNMENT DETAILS (REQUIRED) */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Assignment Details <span className="text-red-500">*</span>
                </label>
                <Input
                  value={assignmentName}
                  onChange={(e) =>
                    handleFieldChange(
                      "assignmentName",
                      e.target.value,
                      setAssignmentName
                    )
                  }
                  placeholder="Enter assignment details"
                  className={errors.assignmentName ? "border-red-500" : ""}
                />
                {errors.assignmentName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.assignmentName}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ASSIGN TO CREW (REQUIRED) */}
                <div className="space-y-2 relative">
                  {" "}
                  {/* <--- ADDED 'relative' HERE */}
                  <label className="text-sm font-medium">
                    Assign to Crew <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Search crew member"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={errors.selectedCrew ? "border-red-500" : ""}
                  />
                  {isDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full border border-gray-300 rounded-lg bg-white shadow-lg max-h-60 overflow-y-auto">
                      {crewList.map((c) => (
                        <div
                          key={c.crew_member_id}
                          className="p-2 cursor-pointer hover:bg-gray-200 text-sm text-black"
                          onClick={() => {
                            handleFieldChange(
                              "selectedCrew",
                              c.crew_member_id,
                              setSelectedCrew
                            );
                            setSearchQuery(`${c.first_name} ${c.last_name}`);
                            setIsDropdownOpen(false);
                          }}
                        >
                          {c.first_name} {c.last_name}
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.selectedCrew && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.selectedCrew}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Rental Type</label>
                  <Select onValueChange={setRentalType}>
                    <SelectTrigger className={`h-10`}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* DATES (REQUIRED) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <DateTimePicker
                    label={
                      <span>
                        Pick-up Date & Time{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    }
                    value={checkoutDateTime}
                    onChange={(val) =>
                      handleFieldChange(
                        "checkoutDateTime",
                        val,
                        setCheckoutDateTime
                      )
                    }
                    error={!!errors.checkoutDateTime}
                  />
                  {errors.checkoutDateTime && (
                    <p className="text-red-500 text-xs">
                      {errors.checkoutDateTime}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <DateTimePicker
                    label={
                      <span>
                        Drop-off Date & Time{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    }
                    value={returnDateTime}
                    onChange={(val) =>
                      handleFieldChange(
                        "returnDateTime",
                        val,
                        setReturnDateTime
                      )
                    }
                    error={!!errors.returnDateTime}
                  />
                  {errors.returnDateTime && (
                    <p className="text-red-500 text-xs">
                      {errors.returnDateTime}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Pick-up Location
                </label>
                <LocationPicker
                  value={pickupLocation}
                  onChange={setPickupLocation}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  placeholder="Any special instructions..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="border border-border bg-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-6">
              Pre-Assignment Checklist
            </h2>
            <div className="flex gap-3 mb-6">
              <Input
                placeholder="Add checklist item..."
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddChecklistItem()}
              />
              <Button
                className="bg-button-coloured text-button-coloured-foreground"
                onClick={handleAddChecklistItem}
              >
                + Add
              </Button>
            </div>

            <div className="space-y-3">
              {checklistItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50/50"
                >
                  <Checkbox
                    checked={item.checked}
                    onCheckedChange={() => toggleChecklistItem(item.id)}
                  />
                  <div className="text-sm">{item.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-border bg-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Button
                className="bg-button-coloured text-button-coloured-foreground w-full py-2"
                onClick={handleConfirmAssignment}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Assigning..." : "Confirm Assignment"}
              </Button>
              <Button
                variant="outline"
                className="bg-button-generic text-button-generic-foreground w-full py-2"
                onClick={onBack}
              >
                Cancel
              </Button>
            </div>
          </div>

          <div className="border border-border bg-card rounded-2xl p-6">
            <div className="flex gap-4 mb-6">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center font-semibold text-sm">
                {equipmentName?.slice(0, 2)?.toUpperCase()}
              </div>
              <div>
                <h3 className="font-bold text-sm">{equipmentName}</h3>
                {!equipmentDetails ? (
                  <p className="text-xs text-muted-foreground">Loading...</p>
                ) : (
                  <>
                    <p className="text-xs">
                      Model: {equipmentDetails.model_number}
                    </p>
                    <p className="text-xs">
                      Category: {equipmentDetails.category_id}
                    </p>
                    <p className="text-xs">
                      Serial: {equipmentDetails.serial_number}
                    </p>
                    <p className="text-xs mt-2">
                      Daily Rate:{" "}
                      <span className="font-semibold">
                        ${equipmentDetails.daily_rental_rate}
                      </span>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="border border-border bg-card rounded-2xl p-6">
            <h3 className="font-semibold mb-4">Rental Calculation</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  Duration{" "}
                  {rentalType
                    ? `(${rentalType === "hourly" ? "Hours" : "Days"})`
                    : ""}
                </div>
                <span className="font-medium">
                  {rentalType === "hourly"
                    ? totalHours
                    : rentalType === "daily"
                      ? totalDays
                      : "--"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign size={16} />
                  {rentalType === "hourly"
                    ? "Hourly Rate"
                    : rentalType === "daily"
                      ? "Daily Rate"
                      : "Select Rental Type"}
                </div>
                <span className="font-medium">
                  {equipmentDetails
                    ? rentalType === "hourly"
                      ? `$${(equipmentDetails.daily_rental_rate / 24).toFixed(
                        2
                      )}`
                      : `$${equipmentDetails.daily_rental_rate}`
                    : "--"}
                </span>
              </div>
              <div className="border-t pt-3 flex justify-between items-center">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 size={16} /> Total Cost
                </div>
                <span className="font-bold">
                  {totalCost > 0 ? `$${totalCost.toFixed(2)}` : "--"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignEquipment;
